import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Media, Sheet, useToast } from '../../components/ui'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { addService, removeService } from '../booking/selectionSlice'
import { useTenant } from '../../tenant/useTenant'
import { cn } from '../../utils/cn'
import { effectivePrice, formatDuration } from '../../utils/format'
import { ServicePrice } from './ServicePrice'
import type { Service } from '../../types/storefront'

export interface ServiceSheetProps {
  service: Service | null
  onClose: () => void
}

/**
 * Service detail is a sheet rather than a route: the customer is browsing a
 * list, and sending them to a page they have to come back from loses their
 * scroll position and their filters.
 */
export function ServiceSheet({ service, onClose }: ServiceSheetProps) {
  const { slug, bootstrap } = useTenant()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const toast = useToast()
  const selected = useAppSelector((s) => s.selection.services)
  const [activeImage, setActiveImage] = useState<string | null>(null)

  const gallery = service ? [service.image, ...service.gallery].filter((src): src is string => Boolean(src)) : []
  const hero = activeImage ?? gallery[0] ?? null
  const inBooking = service ? selected.some((s) => s.id === service.id) : false

  useEffect(() => {
    setActiveImage(null)
  }, [service?.id])

  function handleAdd() {
    if (!service) return
    dispatch(
      addService({
        id: service.id,
        name: service.name,
        duration: service.duration,
        price: effectivePrice(service),
        staffId: null,
        staffName: null,
        favourite: false,
      }),
    )
    onClose()
    navigate(`/${slug}/book`)
  }

  function handleRemove() {
    if (!service) return
    dispatch(removeService(service.id))
    toast.show('Service removed from your appointment.')
  }

  const branchNames = service
    ? bootstrap.branches.filter((b) => service.branch_ids.includes(b.id)).map((b) => b.name)
    : []

  return (
    <Sheet
      open={service !== null}
      onClose={onClose}
      title={service?.name}
      footer={
        service && (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col">
              <ServicePrice service={service} />
              <span className="font-label-caps text-label-caps uppercase text-on-surface-variant">
                {formatDuration(service.duration)}
              </span>
            </div>
            {inBooking ? (
              <div className="flex gap-2">
                <Button variant="outlined" onClick={handleRemove}>
                  Remove
                </Button>
                <Button to={`/${slug}/book`} onClick={onClose}>
                  Continue
                </Button>
              </div>
            ) : (
              <Button icon="add" onClick={handleAdd}>
                Add to booking
              </Button>
            )}
          </div>
        )
      }
    >
      {service && (
        <div className="flex flex-col gap-6">
          <div className="aspect-[4/3] w-full overflow-hidden rounded-xl bg-surface-container-high">
            <Media src={hero} alt={service.name} kind="service" eager />
          </div>

          {gallery.length > 1 && (
            <div className="flex gap-3 overflow-x-auto no-scrollbar">
              {gallery.map((src) => (
                <button
                  key={src}
                  type="button"
                  onClick={() => setActiveImage(src)}
                  aria-label={`View photo of ${service.name}`}
                  className={cn(
                    'h-16 w-16 shrink-0 overflow-hidden rounded-lg border transition-colors',
                    src === hero ? 'border-primary' : 'border-outline-variant/50 hover:border-primary',
                  )}
                >
                  <Media src={src} alt="" kind="service" />
                </button>
              ))}
            </div>
          )}

          {service.category_name && (
            <span className="font-label-caps text-label-caps uppercase text-primary">{service.category_name}</span>
          )}

          {service.description && (
            <p className="whitespace-pre-line font-body-md text-body-md text-on-surface-variant">
              {service.description}
            </p>
          )}

          <dl className="flex flex-col gap-3 border-t border-primary/10 pt-6">
            <div className="flex items-center justify-between gap-4">
              <dt className="font-label-caps text-label-caps uppercase text-on-surface-variant">Duration</dt>
              <dd className="font-body-md text-body-md text-on-surface">{formatDuration(service.duration)}</dd>
            </div>
            <div className="flex items-center justify-between gap-4">
              <dt className="font-label-caps text-label-caps uppercase text-on-surface-variant">Price</dt>
              <dd>
                <ServicePrice service={service} />
              </dd>
            </div>
            {branchNames.length > 0 && (
              <div className="flex items-start justify-between gap-4">
                <dt className="font-label-caps text-label-caps uppercase text-on-surface-variant">Locations</dt>
                <dd className="text-right font-body-md text-body-md text-on-surface">{branchNames.join(', ')}</dd>
              </div>
            )}
          </dl>
        </div>
      )}
    </Sheet>
  )
}
